import { useEffect, useMemo, useState } from 'react';

import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Collapse,
  Grid,
  Group,
  HoverCard,
  Select,
  Text,
  TextInput,
  Tooltip
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import {
  IconAlertTriangle,
  IconChevronDown,
  IconChevronRight,
  IconDeviceFloppy,
  IconPencil,
  IconPlus,
  IconRotateClockwise,
  IconTrash,
  IconX
} from '@tabler/icons-react';

import { useUpdateDeviceLeadsMutation } from '@/store/slices/api/patientsApiSlice';
import {
  useGetDeviceLeadsRecallQuery,
  useGetLeadVendorsListQuery
} from '@/store/slices/api/transmissionReport/transmissionReportsApiSlice';
import { parseImplantDate } from '@/utils/implantDate';
import {
  compareLeadChambers,
  isArchivedLead,
  isInactiveLead,
  prepareLeadsForSave
} from '@/utils/leadChambers';
import { useDeviceAndLeadsRecallFeatureFlag } from '@/hooks/useDeviceAndLeadsRecallFeatureFlag';
import { LeadChamberSelect } from '@/components/LeadChamberSelect';

import { DeviceLeadsRecallResponse, Lead } from '@/types/matchDevice';
import ConfirmationModal from '@/ui/ConfirmationModal';

const byChamber = (left: { leadChamber?: string }, right: { leadChamber?: string }) =>
  compareLeadChambers(left.leadChamber, right.leadChamber);

const LOCATIONS = [
  'Apex',
  'Epicardal',
  'Inflow',
  'Lateral Wall',
  'Outflow',
  'Septum',
  'Appendage',
  'LBB'
];

const calendarDay = (value?: string | null) => {
  if (!value) return null;
  const [year, month, day] = value.slice(0, 10).split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

const formatCalendarDay = (value?: string | null) => {
  const date = calendarDay(value);
  if (!date) return '—';
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${month}-${day}-${date.getFullYear()}`;
};

const toIsoDay = (date: Date | null) => {
  if (!date) return null;
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

const isActive = (lead: Lead) =>
  !isInactiveLead(lead.leadStatus) && !isArchivedLead(lead.leadStatus);

const cappedLabel = (value?: boolean) => (value === true ? 'Yes' : value === false ? 'No' : '—');

const errorMessage = (error: unknown) => {
  const body = error as { data?: { message?: string }; message?: string };
  return body?.data?.message?.trim() || body?.message?.trim() || 'Failed to save lead.';
};

const Field = ({ label, value }: { label: string; value?: string | null }) => (
  <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
    <Text size="xs" c="dimmed">
      {label}
    </Text>
    <Text size="sm">{value || '—'}</Text>
  </Grid.Col>
);

const LeadManager = ({
  deviceId,
  patientId,
  seedLeads,
  onSaved,
  readOnly = false,
  startCollapsed = false
}: {
  deviceId: string;
  patientId: string;
  seedLeads?: Lead[];
  onSaved?: () => void;
  readOnly?: boolean;
  /** Report page: active leads start as one-line rows and open into the full card. */
  startCollapsed?: boolean;
}) => {
  const [leads, setLeads] = useState<Lead[]>(seedLeads ?? []);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Lead | null>(null);
  const [showInactive, setShowInactive] = useState(false);
  const [openLeadIds, setOpenLeadIds] = useState<Record<string, boolean>>({});
  const [leadToInactivate, setLeadToInactivate] = useState<Lead | null>(null);
  const [updateDeviceLeads, { isLoading }] = useUpdateDeviceLeadsMutation();
  const { data: vendors = [] } = useGetLeadVendorsListQuery();
  const { isRecallListPageEnabled } = useDeviceAndLeadsRecallFeatureFlag();
  const { data: leadRecalls } = useGetDeviceLeadsRecallQuery(deviceId, {
    skip: !deviceId || !isRecallListPageEnabled
  });

  const seedKey = JSON.stringify(
    (seedLeads ?? []).map((lead) => ({
      id: lead.id,
      status: lead.leadStatus,
      model: lead.leadModel,
      serial: lead.serialNumber
    }))
  );
  useEffect(() => {
    if (!editingId) setLeads(seedLeads ?? []);
  }, [seedKey, editingId, seedLeads]);

  const vendorOptions = useMemo(
    () =>
      vendors
        .filter((vendor) => vendor.leadVendorName)
        .map((vendor) => ({
          value: String(vendor.leadVendorId),
          label: vendor.leadVendorName as string
        })),
    [vendors]
  );

  const vendorName = (lead: Lead) =>
    lead.leadManufacturer?.leadVendorName ||
    vendorOptions.find(
      (option) => option.value === String(lead.leadManufacturer?.leadVendorId ?? '')
    )?.label ||
    '';

  const recallFor = (lead: Lead) => leadRecalls?.find((recall) => recall.deviceLeadId === lead.id);

  const persist = async (next: Lead[]) => {
    const prepared = prepareLeadsForSave(next, { keepChamberOnly: true });
    if (prepared.error) {
      notifications.show({
        title: prepared.error === 'chamber' ? 'Missing Lead Chamber' : 'Missing Lead Status',
        message:
          prepared.error === 'chamber'
            ? `Please select a Chamber for ${[prepared.missingLead.leadModel, prepared.missingLead.serialNumber].filter(Boolean).join(' / ') || 'the lead with data'}.`
            : 'Please select a Status for all leads with data.',
        color: 'red'
      });
      return false;
    }
    const saving = prepared.leads;

    try {
      await updateDeviceLeads({
        patientId,
        deviceId,
        deviceLeads: saving.map((lead) => ({
          id: lead.id?.startsWith('tempid-') ? undefined : lead.id,
          deviceId,
          leadChamber: lead.leadChamber?.trim() || undefined,
          leadLocation: lead.leadLocation || null,
          leadModel: lead.leadModel,
          serialNumber: lead.serialNumber,
          implantDate: lead.implantDate ? lead.implantDate.slice(0, 10) : null,
          leadStatus: isArchivedLead(lead.leadStatus)
            ? 'Archived'
            : isActive(lead)
              ? 'Active'
              : 'Inactive',
          leadCapped: lead.leadCapped,
          ...(lead.leadPolarity !== undefined && { leadPolarity: lead.leadPolarity || null }),
          leadManufacturer: { leadVendorId: lead.leadManufacturer?.leadVendorId ?? null }
        }))
      }).unwrap();
      setLeads(next);
      setEditingId(null);
      setDraft(null);
      onSaved?.();
      notifications.show({ title: 'Lead saved', message: 'Lead details saved.', color: 'teal' });
      return true;
    } catch (error) {
      notifications.show({ title: 'Lead not saved', message: errorMessage(error), color: 'red' });
      return false;
    }
  };

  const startAdd = () => {
    const id = `tempid-${crypto.randomUUID()}`;
    const lead: Lead = { id, deviceId, leadChamber: '', leadStatus: 'Active' };
    setDraft(lead);
    setEditingId(id);
  };

  const saveDraft = () => {
    if (!draft?.leadChamber?.trim()) {
      notifications.show({
        title: 'Chamber is required',
        message: 'Please enter a chamber name before saving.',
        color: 'red'
      });
      return;
    }
    const next = leads.some((lead) => lead.id === draft.id)
      ? leads.map((lead) => (lead.id === draft.id ? draft : lead))
      : [...leads, draft];
    persist(next);
  };

  const setStatus = (lead: Lead, active: boolean) =>
    persist(
      leads.map((item) =>
        item.id === lead.id ? { ...item, leadStatus: active ? 'Active' : 'Inactive' } : item
      )
    );

  const confirmInactivate = async () => {
    if (!leadToInactivate) return;
    const saved = await setStatus(leadToInactivate, false);
    if (saved) setLeadToInactivate(null);
  };

  const leadBeingRemoved = leadToInactivate
    ? [leadToInactivate.leadChamber, vendorName(leadToInactivate), leadToInactivate.leadModel]
        .filter(Boolean)
        .join(' ')
    : '';

  const visibleLeads = leads.filter((lead) => !isArchivedLead(lead.leadStatus));
  const activeShown = visibleLeads
    .filter((lead) => isActive(lead) || lead.id === editingId)
    .sort(byChamber);
  const inactiveShown = visibleLeads
    .filter((lead) => !isActive(lead) && lead.id !== editingId)
    .sort(byChamber);
  const editing = draft && editingId === draft.id ? draft : null;
  const leadIsOpen = (leadId?: string) => !startCollapsed || (!!leadId && !!openLeadIds[leadId]);
  const toggleLead = (leadId?: string) => {
    if (!leadId) return;
    setOpenLeadIds((current) => ({ ...current, [leadId]: !current[leadId] }));
  };

  return (
    <Box>
      <Group justify="space-between" mb="xs">
        <Text fw={600} size="sm" c="plum.9">
          Leads
        </Text>
        {!readOnly && (
          <Button
            size="compact-sm"
            variant="light"
            color="plum"
            leftSection={<IconPlus size={14} />}
            onClick={startAdd}
            disabled={!!editingId}>
            Add Lead
          </Button>
        )}
      </Group>

      {activeShown.length === 0 && !editing ? (
        <Text size="sm" c="dimmed" mb="sm">
          No active leads. Use Add Lead to record one.
        </Text>
      ) : (
        <Grid gutter="xs">
          {editing && !leads.some((lead) => lead.id === editing.id) && (
            <Grid.Col span={12}>
              <LeadEditCard
                lead={editing}
                vendorOptions={vendorOptions}
                saving={isLoading}
                onChange={setDraft}
                onSave={saveDraft}
                onCancel={() => {
                  setEditingId(null);
                  setDraft(null);
                }}
              />
            </Grid.Col>
          )}
          {activeShown.map((lead) => (
            <Grid.Col span={12} key={lead.id}>
              {editingId === lead.id && editing ? (
                <LeadEditCard
                  lead={editing}
                  vendorOptions={vendorOptions}
                  saving={isLoading}
                  onChange={setDraft}
                  onSave={saveDraft}
                  onCancel={() => {
                    setEditingId(null);
                    setDraft(null);
                  }}
                />
              ) : leadIsOpen(lead.id) ? (
                <LeadReadCard
                  lead={lead}
                  manufacturer={vendorName(lead)}
                  recall={recallFor(lead)}
                  readOnly={readOnly}
                  onCollapse={startCollapsed ? () => toggleLead(lead.id) : undefined}
                  onEdit={() => {
                    setDraft(lead);
                    setEditingId(lead.id ?? null);
                  }}
                  onRemove={() => setLeadToInactivate(lead)}
                />
              ) : (
                <LeadSummaryRow
                  lead={lead}
                  manufacturer={vendorName(lead)}
                  recall={recallFor(lead)}
                  onToggle={() => toggleLead(lead.id)}
                />
              )}
            </Grid.Col>
          ))}
        </Grid>
      )}

      <ConfirmationModal
        isOpen={!!leadToInactivate}
        isLoading={isLoading}
        onClose={() => {
          if (!isLoading) setLeadToInactivate(null);
        }}
        onCancel={() => {
          if (!isLoading) setLeadToInactivate(null);
        }}
        onConfirm={confirmInactivate}
        title="Make this lead inactive?"
        description={
          leadBeingRemoved
            ? `Removing ${leadBeingRemoved} marks it inactive. It stays on the device and can be reactivated later.`
            : 'Removing this lead marks it inactive. It stays on the device and can be reactivated later.'
        }
        primaryButtonText="Make inactive"
        secondaryButtonText="Cancel"
      />

      {inactiveShown.length > 0 && (
        <Box mt="sm">
          <Group
            gap={6}
            mb="xs"
            style={{ cursor: 'pointer', width: 'fit-content' }}
            onClick={() => setShowInactive((open) => !open)}>
            {showInactive ? <IconChevronDown size={15} /> : <IconChevronRight size={15} />}
            <Text fw={600} size="xs" c="dimmed">
              Inactive leads ({inactiveShown.length})
            </Text>
          </Group>
          <Collapse in={showInactive}>
            <Box style={{ border: '1px solid #e2e6ec', borderRadius: 8 }}>
              {inactiveShown.map((lead, index) => (
                <Group
                  key={lead.id}
                  justify="space-between"
                  wrap="nowrap"
                  px="sm"
                  py={8}
                  style={{ borderTop: index === 0 ? undefined : '1px solid #eef1f5' }}>
                  <Box style={{ minWidth: 0 }}>
                    <Group gap={6} wrap="nowrap">
                      <Badge size="xs" variant="light" color="gray" radius="sm" tt="none">
                        Inactive
                      </Badge>
                      <Text size="sm" fw={500} truncate>
                        {[lead.leadChamber, vendorName(lead), lead.leadModel]
                          .filter(Boolean)
                          .join(' · ') || 'Lead'}
                      </Text>
                    </Group>
                    <Text size="xs" c="dimmed" truncate>
                      {lead.serialNumber ? `SN ${lead.serialNumber} · ` : ''}
                      Implanted {formatCalendarDay(lead.implantDate)} · Capped{' '}
                      {cappedLabel(lead.leadCapped)}
                    </Text>
                  </Box>
                  <Group gap={4} wrap="nowrap">
                    <Tooltip label="Edit lead" withArrow>
                      <ActionIcon
                        variant="subtle"
                        color="plum"
                        aria-label="Edit lead"
                        onClick={() => {
                          setDraft(lead);
                          setEditingId(lead.id ?? null);
                        }}>
                        <IconPencil size={15} />
                      </ActionIcon>
                    </Tooltip>
                    <Tooltip label="Reactivate lead" withArrow>
                      <ActionIcon
                        variant="subtle"
                        color="teal"
                        aria-label="Reactivate lead"
                        onClick={() => setStatus(lead, true)}>
                        <IconRotateClockwise size={15} />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                </Group>
              ))}
            </Box>
          </Collapse>
        </Box>
      )}
    </Box>
  );
};

const leadIdentity = (lead: Lead, manufacturer: string) =>
  [lead.leadChamber, manufacturer, lead.leadModel, lead.leadLocation].filter(Boolean).join(' · ') || 'Lead';

const LeadSummaryRow = ({
  lead,
  manufacturer,
  recall,
  onToggle
}: {
  lead: Lead;
  manufacturer: string;
  recall?: DeviceLeadsRecallResponse;
  onToggle: () => void;
}) => (
  <Group
    gap={8}
    wrap="nowrap"
    onClick={onToggle}
    role="button"
    aria-expanded={false}
    style={{
      cursor: 'pointer',
      border: '1px solid #e2e6ec',
      borderRadius: 8,
      padding: '8px 12px',
      background: '#fff'
    }}>
    <IconChevronRight size={15} color="#81899a" style={{ flexShrink: 0 }} />
    <Text size="sm" fw={500} c="#1a1a2e" truncate style={{ flex: 1, minWidth: 0 }}>
      {leadIdentity(lead, manufacturer)}
    </Text>
    {recall && (
      <Box onClick={(event) => event.stopPropagation()}>
        <RecallBadge recall={recall} chamber={lead.leadChamber} />
      </Box>
    )}
  </Group>
);

const LeadReadCard = ({
  lead,
  manufacturer,
  recall,
  readOnly = false,
  onCollapse,
  onEdit,
  onRemove
}: {
  lead: Lead;
  manufacturer: string;
  recall?: DeviceLeadsRecallResponse;
  readOnly?: boolean;
  onCollapse?: () => void;
  onEdit: () => void;
  onRemove: () => void;
}) => (
  <Box
    style={{
      border: '1px solid #e2e6ec',
      borderRadius: 8,
      padding: 12,
      background: '#fff'
    }}>
    <Group justify="space-between" mb={8} wrap="nowrap">
      <Group gap={8} wrap="nowrap">
        {onCollapse && (
          <ActionIcon
            variant="subtle"
            color="gray"
            onClick={onCollapse}
            aria-label="Collapse lead"
            aria-expanded>
            <IconChevronDown size={16} />
          </ActionIcon>
        )}
        <Text size="xs" fw={600} c="dimmed">
          Lead {lead.leadChamber ? `· ${lead.leadChamber}` : ''}
        </Text>
        {recall && <RecallBadge recall={recall} chamber={lead.leadChamber} />}
      </Group>
      {!readOnly && (
        <Group gap={4} wrap="nowrap">
          <Tooltip label="Edit lead" withArrow>
            <ActionIcon variant="subtle" color="plum" onClick={onEdit} aria-label="Edit lead">
              <IconPencil size={16} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Remove lead" withArrow>
            <ActionIcon variant="subtle" color="red" onClick={onRemove} aria-label="Remove lead">
              <IconTrash size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>
      )}
    </Group>
    <Grid gutter="xs">
      <Field label="Chamber" value={lead.leadChamber} />
      <Field label="Manufacturer" value={manufacturer} />
      <Field label="Lead Model" value={lead.leadModel} />
      <Field label="Serial Number" value={lead.serialNumber} />
      <Field label="Implant Date" value={formatCalendarDay(lead.implantDate)} />
      <Field label="Lead Location" value={lead.leadLocation} />
      <Field label="Lead Capped?" value={cappedLabel(lead.leadCapped)} />
    </Grid>
  </Box>
);

const LeadEditCard = ({
  lead,
  vendorOptions,
  saving,
  onChange,
  onSave,
  onCancel
}: {
  lead: Lead;
  vendorOptions: { value: string; label: string }[];
  saving: boolean;
  onChange: (lead: Lead) => void;
  onSave: () => void;
  onCancel: () => void;
}) => {
  const set = (patch: Partial<Lead>) => onChange({ ...lead, ...patch });
  return (
    <Box
      style={{ border: '1px solid #e2e6ec', borderRadius: 8, padding: 12, background: '#f7f8fa' }}>
      <Text size="xs" fw={600} c="dimmed" mb={8}>
        {lead.leadChamber ? `Lead · ${lead.leadChamber}` : 'Lead'}
      </Text>
      <Grid gutter="xs">
        <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
          <LeadChamberSelect
            value={lead.leadChamber}
            onChange={(value) => set({ leadChamber: value })}
            label="Chamber"
            size="xs"
            placeholder="Select chamber"
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
          <Select
            label="Manufacturer"
            size="xs"
            data={vendorOptions}
            value={
              lead.leadManufacturer?.leadVendorId != null
                ? String(lead.leadManufacturer.leadVendorId)
                : null
            }
            onChange={(value) => {
              const vendor = vendorOptions.find((option) => option.value === value);
              set({
                leadManufacturer: value
                  ? { leadVendorId: Number(value), leadVendorName: vendor?.label }
                  : undefined
              });
            }}
            searchable
            clearable
            placeholder="Select…"
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
          <TextInput
            label="Lead Model"
            size="xs"
            value={lead.leadModel ?? ''}
            onChange={(event) => set({ leadModel: event.currentTarget.value })}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
          <TextInput
            label="Serial Number"
            size="xs"
            value={lead.serialNumber ?? ''}
            onChange={(event) => set({ serialNumber: event.currentTarget.value })}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
          <DateInput
            label="Implant Date"
            size="xs"
            valueFormat="MM/DD/YYYY"
            placeholder="MM/DD/YYYY"
            dateParser={parseImplantDate}
            value={parseImplantDate(lead.implantDate)}
            onChange={(date) => set({ implantDate: toIsoDay(date) })}
            clearable
            popoverProps={{ withinPortal: true }}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
          <Select
            label="Lead Location"
            size="xs"
            data={LOCATIONS}
            value={lead.leadLocation || null}
            onChange={(value) => set({ leadLocation: value })}
            clearable
            placeholder="—"
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
          <Select
            label="Lead Capped?"
            size="xs"
            data={[
              { value: 'yes', label: 'Yes' },
              { value: 'no', label: 'No' }
            ]}
            value={lead.leadCapped === true ? 'yes' : lead.leadCapped === false ? 'no' : null}
            onChange={(value) =>
              set({ leadCapped: value === 'yes' ? true : value === 'no' ? false : undefined })
            }
            clearable
            placeholder="—"
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
          <Select
            label="Status"
            size="xs"
            data={[
              { value: 'Active', label: 'Active' },
              { value: 'Inactive', label: 'Inactive' }
            ]}
            value={isActive(lead) ? 'Active' : 'Inactive'}
            onChange={(value) => set({ leadStatus: value === 'Inactive' ? 'Inactive' : 'Active' })}
            allowDeselect={false}
          />
        </Grid.Col>
      </Grid>
      <Group justify="flex-end" gap={8} mt={10}>
        <Button
          size="xs"
          variant="default"
          leftSection={<IconX size={14} />}
          onClick={onCancel}
          disabled={saving}>
          Cancel
        </Button>
        <Button
          size="xs"
          color="plum"
          leftSection={<IconDeviceFloppy size={14} />}
          onClick={onSave}
          loading={saving}>
          Save lead
        </Button>
      </Group>
    </Box>
  );
};

const RecallBadge = ({
  recall,
  chamber
}: {
  recall: DeviceLeadsRecallResponse;
  chamber?: string;
}) => {
  const heading =
    chamber === 'RA'
      ? 'Right Atrium Lead Recall'
      : chamber === 'RV'
        ? 'Right Ventricle Lead Recall'
        : chamber === 'LV'
          ? 'Left Ventricle Lead Recall'
          : `${chamber || 'Lead'} Recall`;
  return (
    <HoverCard width={320} position="top" withArrow shadow="md" openDelay={100} closeDelay={200}>
      <HoverCard.Target>
        <Badge
          color="red"
          variant="filled"
          radius="sm"
          leftSection={<IconAlertTriangle size={12} />}
          style={{ cursor: 'pointer' }}>
          {recall.recallClass || 'Recall'}
        </Badge>
      </HoverCard.Target>
      <HoverCard.Dropdown>
        <Text size="sm" fw={700} c="red.7" mb={4}>
          {heading}
        </Text>
        <Text size="xs" mb={2}>
          <b>Recall Class:</b> {recall.recallClass || '-'}
        </Text>
        <Text size="xs" mb={2}>
          <b>Recall Initiation Date:</b> {formatCalendarDay(recall.recallInitiationDate)}
        </Text>
        <Text size="xs" mb={2}>
          <b>Recall Status:</b> {recall.recallStatus || '-'}
        </Text>
        <Text size="xs">
          <b>Reason For Recall:</b> {recall.reasonForRecall || '-'}
        </Text>
      </HoverCard.Dropdown>
    </HoverCard>
  );
};

export default LeadManager;
